import { SearchTreeNode } from '@/types/searchTreeTypes';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Helmet } from 'react-helmet';

import BlockerDialog from '@components/BlockerDialog';
import ButtonRow from '@components/ButtonRow';
import EditCriteriaWarningModal from '@components/EditCriteriaWarningModal';
import LoadSearchTemplateDialog from '@components/LoadSearchTemplateDialog/LoadSearchTemplateDialog';
import OtherActionsPanelContentAdv from '@components/OtherActionsPanelContentAdv';
import SaveSearchDialog from '@components/SaveSearchDialog';
import UpdateSearchDialog from '@components/SaveSearchDialog/UpdateSearchDialog';
import Sidebar from '@components/Sidebar';
import TemplateValidationWarningModal from '@components/TemplateValidationWarningModal';
import ToggleDebugButton from '@components/ToggleDebugButton';

import PreviewSection from './PreviewSection';
import SearchTreeBuilder from './SearchTreeBuilder';
import { AdvancedSearchValidationDialog } from './ValidationDialog';
import { useAdvancedSearchListCreatorPage } from './useAdvancedSearchListCreatorPage';

type AdvancedSearchListCreatorLayoutProps = {
  page: ReturnType<typeof useAdvancedSearchListCreatorPage>;
  searchTreeBuilderTestId?: string;
  previewSectionTestId?: string;
};

export default function AdvancedSearchListCreatorLayout({
  page,
  searchTreeBuilderTestId,
  previewSectionTestId,
}: AdvancedSearchListCreatorLayoutProps) {
  const pageHeader =
    page.searchTemplateData !== undefined ? (
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography component="h2" variant="h5">
          New Search -{' '}
          <em>
            from the template{' '}
            <strong>{`${page.searchMetadata.name ?? ''}${page.asteriskIndicatingMod}`}</strong>
          </em>
        </Typography>
        {page.userDetails?.userLevel === 'Principal Coordinator' && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={page.navigateToTemplatePage}
          >
            Create template
          </Button>
        )}
      </Stack>
    ) : (
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography component="h2" variant="h5">
          {`${page.searchMetadata.name ?? 'New Search'}${page.asteriskIndicatingMod}`}
        </Typography>
        {page.userDetails?.userLevel === 'Principal Coordinator' && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={page.navigateToTemplatePage}
          >
            Create template
          </Button>
        )}
      </Stack>
    );

  const sidebarTree: SearchTreeNode | undefined =
    page.selectedContactIds.length > 0
      ? { op: 'filter', filter: { name: 'contactIds', value: page.selectedContactIds } }
      : page.activeSearchTree;

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>List Manager - Advanced Search</title>
        <meta name="description" content="List Manager - Advanced Search" />
      </Helmet>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          height: "inherit",
          justifyContent: "space-between",
          width: '100%',
          minWidth: 0
        }}>
        {page.enableDevFeatures && (
          <ToggleDebugButton debug={page.debug} setDebug={page.setDebug} sx={{ position: 'absolute' }} />
        )}

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            padding: "2rem",
            minWidth: 0,
            width: '100%',
            boxSizing: 'border-box'
          }}>
          {pageHeader}

          {page.isTemplateWarningModalOpen && page.searchTemplateData !== undefined && (
            <TemplateValidationWarningModal
              searchTemplateData={page.searchTemplateData}
              onProceed={() => page.setIsTemplateWarningModalOpen(false)}
            />
          )}

          {page.isEditCriteriaModalOpen && (
            <EditCriteriaWarningModal
              isOpen={page.isEditCriteriaModalOpen}
              onCancel={() => {
                page.revertEditCriteriaChange();
                page.setIsEditCriteriaModalOpen(false);
              }}
              onProceed={() => {
                page.setHasProceededFromEditCriteria(true);
                page.setIsEditCriteriaModalOpen(false);
              }}
              onRestart={async () => {
                page.setIsEditCriteriaModalOpen(false);
                // eslint-disable-next-line react-hooks/immutability -- flag a restart from the edit-criteria modal
                page.restartFromEditCriteriaRef.current = true;
                await page.handleRestartClick();
              }}
              showRestartButton={true}
            />
          )}

          {page.isValidationDialogOpen && !page.hasTemplateErrors && <AdvancedSearchValidationDialog />}

          <Box className="advancedSearchBuilder" sx={{ marginTop: '24px', minWidth: 0 }}>
            <Accordion
              expanded={page.expandCriteriaAccordion}
              square={true}
              sx={{ borderRadius: '4px' }}
              onChange={page.toggleCriteriaAccordion}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="criteria-accordion-summary">
                <Typography component="h3" variant="h6">
                  {page.expandCriteriaAccordion ? 'Criteria' : 'Criteria (expand to edit)'}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <SearchTreeBuilder
                  onSearchTreeChange={page.handleBuilderSearchTreeChange}
                  debug={page.debug}
                  data-testid={searchTreeBuilderTestId}
                  searchTreeBuilderState={page.searchTreeBuilderState}
                  setSearchTreeBuilderState={page.setSearchTreeBuilderState}
                />
              </AccordionDetails>
            </Accordion>
          </Box>

          <ButtonRow noMarginX>
            <Button variant="outlined" disabled={!page.isPreviewFetching} onClick={page.handleCancel}>
              Cancel
            </Button>
            {page.showRestartButton && (
              <Button
                variant="outlined"
                onClick={page.handleRestartClick}
                data-testid="restart-button"
              >
                Restart
              </Button>
            )}
            <Button
              variant="outlined"
              disabled={page.isSaveSearchDisabled}
              onClick={page.handleSaveSearchDialogOpen}
            >
              Save Search
            </Button>
            <Tooltip arrow title={page.isFilterIncomplete ? 'One or more of your filters is incomplete' : ''}>
              <span>
                <Button variant="contained" onClick={page.handleApply} disabled={page.isApplyDisabled}>
                  Apply
                </Button>
              </span>
            </Tooltip>
          </ButtonRow>

          {page.debug && (
            <Grid container spacing={0}>
              <Grid size={6}>
                <Accordion>
                  <AccordionSummary>
                    <Typography component="span">Builder search tree</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ fontSize: '10px' }}>
                    <pre>
                      {page.builderSearchTree
                        ? JSON.stringify(page.builderSearchTree, null, 4)
                        : 'Undefined'}
                    </pre>
                  </AccordionDetails>
                </Accordion>
              </Grid>
              <Grid size={6}>
                <Accordion>
                  <AccordionSummary>
                    <Typography component="span">Active search tree</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ fontSize: '10px' }}>
                    <pre>
                      {page.activeSearchTree
                        ? JSON.stringify(page.activeSearchTree, null, 4)
                        : 'Undefined'}
                    </pre>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          )}

          <Paper sx={{ padding: '16px', minWidth: 0, maxWidth: '100%' }} elevation={1}>
            <Typography component="h3" variant="h6">
              Results
            </Typography>
            <PreviewSection
              isFetching={page.isPreviewFetching}
              listPreview={page.listPreview}
              setSelectedContactIds={page.setSelectedContactIds}
              selectedContactIds={page.selectedContactIds}
              activeSearchTree={page.activeSearchTree}
              setSortModel={page.setSortModel}
              sortModel={page.sortModel}
              filterModel={page.filterModel}
              setFilterModel={page.setFilterModel}
              isSaveListDialogOpen={page.isSaveListDialogOpen}
              setIsSaveListDialogOpen={page.setIsSaveListDialogOpen}
              onAllowNavigationBeforeLeave={page.allowNextNavigation}
              data-testid={previewSectionTestId}
            />
          </Paper>

          <ButtonRow noMarginX sx={{ paddingBottom: '16px !important' }}>
            {['Coordinator', 'Principal Coordinator'].includes(page.userDetails?.userLevel ?? 'None') && (
              <Button
                variant="outlined"
                disabled={page.areBothButtonsDisabled || page.isOtherActionsPanelOpen}
                onClick={page.handleClickOtherActions}
              >
                Other Actions
              </Button>
            )}
            <Button
              variant="contained"
              onClick={page.handleClickSaveToList}
              disabled={page.areBothButtonsDisabled}
            >
              Save To List
            </Button>
          </ButtonRow>
        </Box>

        <Sidebar
          isOpen={page.isOtherActionsPanelOpen}
          title="Other actions"
          testId="rightSidePanel"
          position="right"
          floating
          noTopBorder
        >
          <OtherActionsPanelContentAdv
            onClose={() => page.setIsOtherActionsPanelOpen(false)}
            tree={sidebarTree}
            sortOrder={page.sortModel?.[0]?.sort ?? 'asc'}
            sortColumn={page.sortModel?.[0]?.field ?? 'id'}
          />
        </Sidebar>
      </Box>

      <SaveSearchDialog
        dialogStatus={page.saveSearchDialogStatus}
        onClose={page.handleSaveSearchDialogClose}
        search={page.activeSearchTree}
        key={page.saveSearchDialogStatus}
        currentSearchMetadata={page.searchMetadata}
        searchType={'Advanced'}
      />

      <UpdateSearchDialog
        isOpen={page.isUpdateSearchDialogOpen}
        onClose={(isSaved: boolean) => {
          page.setIsUpdateSearchDialogOpen(false);
          if (isSaved) page.setIsSearchModified(false);
        }}
        openSaveSearchDialog={({ prefill }: { prefill: boolean }) => {
          page.setSaveSearchDialogStatus(prefill ? 'prefilled' : 'open');
        }}
        search={page.activeSearchTree}
        searchMetadata={page.searchMetadata}
      />

      <BlockerDialog blocker={page.blocker} popStateBlock={page.popStateBlock} />

      {page.restartTemplateForLoad && (
        <LoadSearchTemplateDialog
          searchTemplate={page.restartTemplateForLoad}
          onClose={() => page.setRestartTemplateForLoad(undefined)}
          onCloseWithoutApply={
            page.restartFromEditCriteriaRef.current
              ? () => {
                  page.revertEditCriteriaChange();
                  // eslint-disable-next-line react-hooks/immutability -- clear the restart-from-edit-criteria flag
                  page.restartFromEditCriteriaRef.current = false;
                }
              : undefined
          }
          fromRestart
          previousSelectionsTree={page.searchTemplateData?.initialTree}
        />
      )}
    </>
  );
}

